import type { Account } from '../../../../accounts';
export interface UsersPageProps {
  accounts?: import('../../../../accounts').AccountsState;
  getLearnerData?: (learnerId: string) => import('../../../../app/types').AppState | null;
  getLearnerModuleMastery?: (learnerId: string, moduleId: import('../../../../content').ModuleId) => number;
  onNavigateToLearner?: (learnerId: string) => void;
}
export type RoleFilter = 'all' | 'learner' | 'teacher' | 'parent' | 'admin' | 'school';
export interface FamilyMasteryEntry {
  masteryScore: number;
  attempts: number;
  familyId: string;
}
export interface UserRow {
  id: string;
  name: string;
  email: string;
  role: 'learner' | 'teacher' | 'parent' | 'admin';
  organization: string;
  linkedCount: number;
  xp: number;
  lastActive: number;
  lastActiveFormatted: string;
  /**
   * GEEN `status`-veld meer.
   *
   * Dat stond hier als `'active' | 'inactive'` en werd door beide bouwers
   * onvoorwaardelijk op `'active'` gezet — nooit berekend, nooit gelezen. Het
   * probleem is niet de dode regel maar wat de volgende ontwikkelaar ermee doet:
   * een veld dat "status" heet, ziet eruit alsof het iets weet. Wie er een badge
   * aan hangt, krijgt een tabel waarin élk account "actief" heet, ook een
   * leerling die een jaar niets heeft gedaan.
   *
   * Er IS een echte definitie van actief — het venster van 7 dagen dat
   * `useDashboardData` op `lastActive` toepast. Wie hier een status wil, leidt
   * die daar af; `lastActive` staat hierboven.
   */
  account?: Account;
  avgMastery?: number;
  sessionCount?: number;
  groupName?: string;
  familyMastery?: Record<string, FamilyMasteryEntry>;
  /**
   * Dit account staat op de server maar niet in de lokale store.
   *
   * De rij is dan bewust mager: geen relaties, geen leerdata, geen
   * bewerkformulier. Dat is geen ontbrekende functionaliteit maar de grens van
   * wat de server zonder break glass prijsgeeft.
   */
  isServerOnly?: boolean;
}
export interface SchoolData {
  name: string;
  learnerCount: number;
  teacherCount: number;
  avgMastery: number;
  learners: UserRow[];
  teachers: UserRow[];
}
/**
 * Rolweergave in het adminportaal.
 *
 * De LABELS komen uit `accounts/logic/uiHelpers.getAccountRoleLabel` — de
 * gedeelde bron die ook de zijbalk, de profielknop en de onboarding gebruiken.
 * Ze stonden hier tot 12 aug 2026 als losse strings, en waren gedrift: een
 * beheerder heette in dit portaal **"Admin"** en in de rest van de app
 * **"Beheerder"**. Eén rol, twee namen, in één product.
 *
 * Dat is precies de soort drift die niemand als bug meldt (beide woorden zijn
 * begrijpelijk) en die tegelijk het duurst is om later te herstellen: zodra
 * ergens "Admin" in een export, screenshot of instructie staat, wordt het een
 * tweede term die mensen gaan gebruiken.
 *
 * De Badge-`variant` blijft hier: dat is een adminspecifieke keuze over
 * kleuraccent in een tabel, geen naamgeving. Wat weg is: het `icon`-veld — dat
 * hield icoonnamen als STRING vast ('Users', 'Shield') die niemand las, terwijl
 * `getAccountIcon` het echte component levert. Een tweede, niet-gebruikte
 * icoonlijst is een uitnodiging om af te wijken van de eerste.
 */
import { getAccountRoleLabel } from '../../../../accounts/logic/uiHelpers';
export const ROLE_CONFIG = {
  learner: {
    label: getAccountRoleLabel('learner'),
    variant: 'info' as const
  },
  teacher: {
    label: getAccountRoleLabel('teacher'),
    variant: 'secondary' as const
  },
  parent: {
    label: getAccountRoleLabel('parent'),
    variant: 'success' as const
  },
  admin: {
    label: getAccountRoleLabel('admin'),
    variant: 'warning' as const
  }
} as const;
export type RoleConfigKey = keyof typeof ROLE_CONFIG;